import React from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { LoadingPlaceholder, theme } from '@sg/garnish';

import {
  BUNDLE_FOOTER_MIN_BOTTOM_SPACING,
  BUNDLE_HERO_ASPECT_RATIO,
  BUNDLE_TRANSITION_DURATION,
} from '../../Bundle.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleLoading = () => {
  const { width: windowWidth } = useWindowDimensions();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Derived ─────────────────────────────────────────────────────────

  const heroHeight = windowWidth / BUNDLE_HERO_ASPECT_RATIO;

  // ─── Styles ──────────────────────────────────────────────────────────

  const footerDynamicStyles = useStyle(
    () => ({ paddingBottom: Math.max(BUNDLE_FOOTER_MIN_BOTTOM_SPACING, safeAreaBottomInset) }),
    [safeAreaBottomInset],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={styles.container} entering={FADE_IN} exiting={FADE_OUT}>
      <LoadingPlaceholder rows={1} rowHeight={heroHeight} borderRadius={0} palette="cream" />

      <View style={styles.copy}>
        <LoadingPlaceholder
          rows={1}
          rowHeight={NAME_HEIGHT}
          borderRadius={theme.radius.medium}
          palette="cream"
          style={styles.name}
        />

        <LoadingPlaceholder
          rows={DESCRIPTION_ROWS}
          rowHeight={DESCRIPTION_ROW_HEIGHT}
          gridGapY={theme.spacing['1']}
          borderRadius={theme.radius.medium}
          palette="cream"
        />
      </View>

      <View style={styles.included}>
        <View style={styles.header}>
          <LoadingPlaceholder
            rows={1}
            rowHeight={HEADER_HEIGHT}
            borderRadius={theme.radius.medium}
            palette="cream"
            style={styles.headerTitle}
          />

          <LoadingPlaceholder
            rows={1}
            rowHeight={BADGE_SIZE}
            borderRadius={BADGE_SIZE / 2}
            palette="cream"
            style={styles.badge}
          />
        </View>

        <View style={styles.slots}>
          <LoadingPlaceholder
            rows={1}
            rowHeight={SLOT_HEIGHT}
            borderRadius={theme.radius.xlarge}
            palette="cream"
          />
        </View>
      </View>

      <View style={[styles.footer, footerDynamicStyles]}>
        <LoadingPlaceholder
          rows={1}
          rowHeight={BUTTON_HEIGHT}
          borderRadius={BUTTON_HEIGHT / 2}
          palette="cream"
        />
      </View>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const FADE_IN = FadeIn.duration(BUNDLE_TRANSITION_DURATION);
const FADE_OUT = FadeOut.duration(BUNDLE_TRANSITION_DURATION);

const NAME_HEIGHT = 36;
const NAME_WIDTH = '60%';

const DESCRIPTION_ROWS = 2;
const DESCRIPTION_ROW_HEIGHT = 20;

const HEADER_HEIGHT = 31;
const HEADER_TITLE_WIDTH = 140;

const BADGE_SIZE = 24;
const SLOT_HEIGHT = 56;
const BUTTON_HEIGHT = 64;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  copy: {
    gap: theme.spacing['1'],
    paddingTop: theme.spacing['4'],
    paddingHorizontal: theme.spacing['6'],
  },
  name: {
    width: NAME_WIDTH,
  },
  included: {
    gap: theme.spacing['4'],
    paddingTop: theme.spacing['6'],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingHorizontal: theme.spacing['6'],
  },
  headerTitle: {
    width: HEADER_TITLE_WIDTH,
  },
  badge: {
    width: BADGE_SIZE,
  },
  slots: {
    paddingHorizontal: theme.spacing['4'],
  },
  footer: {
    marginTop: 'auto',
    paddingTop: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
});
